import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart3, PieChart, Search, Download, Loader2, Brain,
  TrendingUp, Users, Target, Lightbulb, AlertTriangle, CheckCircle
} from "lucide-react";
import TopNav from "@/components/TopNav";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, Legend
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const GAP_COLORS = {
  low: "#22c55e",
  medium: "#eab308",
  high: "#ef4444",
};

const PIE_COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899"];

function GapColorBadge({ pct }) {
  if (pct < 30) return <Badge className="bg-green-100 text-green-700 border-green-200">{pct}%</Badge>;
  if (pct < 60) return <Badge className="bg-amber-100 text-amber-700 border-amber-200">{pct}%</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200">{pct}%</Badge>;
}

export default function HRIntelligence() {
  const [activeTab, setActiveTab] = useState("gap-analysis");

  // Gap Analysis
  const [gapData, setGapData] = useState(null);
  const [gapDept, setGapDept] = useState("");
  const [loadingGap, setLoadingGap] = useState(false);

  // Position Matching
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [matchData, setMatchData] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);

  // Capacity Analysis
  const [capacityData, setCapacityData] = useState(null);
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  // Skills Requirements
  const [positions, setPositions] = useState([]);
  const [skillSearch, setSkillSearch] = useState("");
  const [requirementResults, setRequirementResults] = useState([]);

  useEffect(() => {
    fetchMembers();
    if (activeTab === "gap-analysis") fetchGapAnalysis();
    if (activeTab === "capacity") fetchCapacityAnalysis();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "skills") fetchPositions();
  }, [activeTab]);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/api/hr/members`, { headers: getHeaders(), withCredentials: true });
      setMembers(res.data);
    } catch {}
  };

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/api/hr/positions`, { headers: getHeaders(), withCredentials: true });
      setPositions(res.data);
    } catch {}
  };

  const fetchGapAnalysis = async (department) => {
    setLoadingGap(true);
    try {
      const url = department
        ? `${API}/api/hr/intelligence/gap-analysis/${encodeURIComponent(department)}`
        : `${API}/api/hr/intelligence/gap-analysis`;
      const res = await axios.get(url, { headers: getHeaders(), withCredentials: true });
      setGapData(res.data);
    } catch (err) {
      toast.error("Failed to load gap analysis");
    }
    setLoadingGap(false);
  };

  const fetchPositionMatch = async (memberId) => {
    if (!memberId) return;
    setLoadingMatch(true);
    try {
      const res = await axios.get(`${API}/api/hr/intelligence/position-match/${memberId}`,
        { headers: getHeaders(), withCredentials: true }
      );
      setMatchData(res.data);
    } catch (err) {
      toast.error("Failed to load position matches");
    }
    setLoadingMatch(false);
  };

  const fetchCapacityAnalysis = async () => {
    setLoadingCapacity(true);
    try {
      const res = await axios.get(`${API}/api/hr/intelligence/capacity-analysis`,
        { headers: getHeaders(), withCredentials: true }
      );
      setCapacityData(res.data);
    } catch (err) {
      toast.error("Failed to load capacity analysis");
    }
    setLoadingCapacity(false);
  };

  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API}/api/hr/intelligence/export/${type}`,
        { headers: { ...getHeaders() }, withCredentials: true, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handleSkillSearch = () => {
    if (!skillSearch.trim() || !positions.length) return;
    const q = skillSearch.toLowerCase();
    const results = [];
    positions.forEach(pos => {
      const skills = (pos.skills_required || []).map(s => s.toLowerCase());
      const quals = (pos.qualifications_required || []).map(q => q.toLowerCase());
      if (skills.some(s => s.includes(q)) || quals.some(q => q.includes(q))) {
        members.forEach(m => {
          if (m.position_id === pos.id || m.post_number === pos.post_number) {
            const memberSkills = (m.skills || []).map(s => s.toLowerCase());
            const memberQuals = (m.qualifications || []).map(q => q.toLowerCase());
            results.push({
              position: pos.title || pos.post_number,
              department: pos.department || "",
              required: [...(pos.skills_required || []), ...(pos.qualifications_required || [])],
              member: m.name || "",
              memberHas: [...memberSkills, ...memberQuals].filter(v => v.includes(q)),
              coverage: memberSkills.some(s => s.includes(q)) || memberQuals.some(q => q.includes(q)) ? "yes" : "no",
            });
          }
        });
        if (!results.some(r => r.position === (pos.title || pos.post_number))) {
          results.push({
            position: pos.title || pos.post_number,
            department: pos.department || "",
            required: [...(pos.skills_required || []), ...(pos.qualifications_required || [])],
            member: "Unfilled",
            memberHas: [],
            coverage: "unfilled",
          });
        }
      }
    });
    setRequirementResults(results);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-['Barlow_Condensed'] font-bold text-slate-800 tracking-wide">
            HR Intelligence
          </h1>
          <p className="text-sm text-slate-500">AI-powered workforce analytics</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="gap-analysis" className="data-[state=active]:bg-white">
            <Target className="w-4 h-4 mr-2" /> Gap Analysis
          </TabsTrigger>
          <TabsTrigger value="position-match" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" /> Position Matching
          </TabsTrigger>
          <TabsTrigger value="capacity" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" /> Capacity Analysis
          </TabsTrigger>
          <TabsTrigger value="skills" className="data-[state=active]:bg-white">
            <Brain className="w-4 h-4 mr-2" /> Skills Requirements
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Gap Analysis */}
        <TabsContent value="gap-analysis" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Filter by department"
                  className="w-60"
                  value={gapDept}
                  onChange={(e) => setGapDept(e.target.value)}
                />
                <Button onClick={() => fetchGapAnalysis(gapDept)} className="bg-emerald-500 text-white" size="sm">
                  <Search className="w-4 h-4 mr-1" /> Analyze
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport("gap-analysis")}>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>

            {loadingGap ? (
              <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
            ) : gapData ? (
              <div className="p-4 space-y-4">
                {/* Averages */}
                {gapData.overall_averages && (
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(gapData.overall_averages).map(([key, val]) => (
                      <Card key={key} className="p-3 border-slate-200 text-center">
                        <p className="text-xs text-slate-400 capitalize">{key}</p>
                        <p className="text-lg font-bold text-slate-700">{val}%</p>
                      </Card>
                    ))}
                  </div>
                )}

                {/* AI Summary */}
                {gapData.ai_summary && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2 text-purple-700 font-medium mb-1">
                      <Brain className="w-4 h-4" /> AI Analysis
                    </div>
                    <p className="text-sm text-purple-600">{gapData.ai_summary}</p>
                  </div>
                )}

                {/* Results table */}
                <ScrollArea className="max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-app text-xs text-slate-500 uppercase sticky top-0">
                      <tr>
                        <th className="text-left p-2">Member</th>
                        <th className="text-left p-2">Position</th>
                        <th className="text-center p-2">Knowledge (25%)</th>
                        <th className="text-center p-2">Skills (25%)</th>
                        <th className="text-center p-2">Experience (20%)</th>
                        <th className="text-center p-2">Qualification (20%)</th>
                        <th className="text-center p-2">Abilities (10%)</th>
                        <th className="text-center p-2">Gap %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(gapData.results || []).slice(0, 100).map((r, idx) => (
                        <tr key={idx} className="hover:bg-app">
                          <td className="p-2 font-medium text-slate-700">{r.member_name}</td>
                          <td className="p-2 text-slate-500 text-xs">{r.post_number || r.position_title}</td>
                          <td className="p-2 text-center"><GapColorBadge pct={r.dimensions.knowledge.score} /></td>
                          <td className="p-2 text-center"><GapColorBadge pct={r.dimensions.skills.score} /></td>
                          <td className="p-2 text-center"><GapColorBadge pct={r.dimensions.experience.score} /></td>
                          <td className="p-2 text-center"><GapColorBadge pct={r.dimensions.qualification.score} /></td>
                          <td className="p-2 text-center"><GapColorBadge pct={r.dimensions.abilities.score} /></td>
                          <td className="p-2 text-center">
                            <GapColorBadge pct={r.overall_gap_pct} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            ) : (
              <div className="text-center p-12 text-slate-400">Click "Analyze" to run gap analysis</div>
            )}
          </Card>
        </TabsContent>

        {/* Tab 2: Position Matching */}
        <TabsContent value="position-match" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Select value={selectedMember} onValueChange={(v) => { setSelectedMember(v); fetchPositionMatch(v); }}>
                  <SelectTrigger className="w-80">
                    <SelectValue placeholder="Select a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name} ({m.employee_number || m.id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {loadingMatch && <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />}
              </div>
            </div>

            {matchData ? (
              <div className="p-4 space-y-3">
                <p className="text-sm text-slate-500">
                  Evaluating {matchData.member_name} against {matchData.total_positions_evaluated} positions
                </p>
                <ScrollArea className="max-h-96">
                  <div className="space-y-2">
                    {(matchData.top_matches || []).map((m, idx) => (
                      <Card key={idx} className="p-3 border-slate-200 hover:border-emerald-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-700 text-sm">{m.position_title}</p>
                            <p className="text-xs text-slate-400">{m.post_number} — {m.department} — {m.level_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-600">{m.match_pct}%</p>
                            <p className="text-xs text-slate-400">match</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          {Object.entries(m.dimensions).map(([key, val]) => (
                            <Badge key={key} variant="outline" className="text-xs">
                              {key}: {val}%
                            </Badge>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <div className="text-center p-12 text-slate-400">Select a member to see position matches</div>
            )}
          </Card>
        </TabsContent>

        {/* Tab 3: Capacity Analysis */}
        <TabsContent value="capacity" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => handleExport("capacity-analysis")}>
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>

          {loadingCapacity ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
          ) : capacityData ? (
            <>
              {/* AI Summary */}
              {capacityData.ai_summary && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2 text-purple-700 font-medium mb-1">
                    <Brain className="w-4 h-4" /> AI Analysis
                  </div>
                  <p className="text-sm text-purple-600">{capacityData.ai_summary}</p>
                </div>
              )}

              {/* Department Capacity Chart */}
              <Card className="bg-white border-slate-200">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-700">Department Capacity</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={capacityData.department_capacity || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="department" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="filled" name="Filled" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="vacant" name="Vacant" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Level Breakdown */}
              <Card className="bg-white border-slate-200">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-700">Level Composition</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <RePieChart>
                        <Pie
                          data={(capacityData.level_capacity || []).map(l => ({ name: l.level_code, value: l.positions }))}
                          cx="50%" cy="50%" outerRadius={80} dataKey="value"
                        >
                          {(capacityData.level_capacity || []).map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {(capacityData.level_capacity || []).map((l, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{l.level_code}</span>
                          <span className="text-slate-800 font-medium">{l.filled}/{l.positions} ({l.fill_rate_pct}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <div className="text-center p-12 text-slate-400">Loading capacity data...</div>
          )}
        </TabsContent>

        {/* Tab 4: Skills Requirements */}
        <TabsContent value="skills" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Input
                  placeholder="Search skill or qualification..."
                  className="w-80"
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSkillSearch()}
                />
                <Button onClick={handleSkillSearch} className="bg-emerald-500 text-white" size="sm">
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
              </div>
            </div>
            {requirementResults.length > 0 ? (
              <div className="p-4">
                <ScrollArea className="max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-app text-xs text-slate-500 uppercase sticky top-0">
                      <tr>
                        <th className="text-left p-2">Position</th>
                        <th className="text-left p-2">Department</th>
                        <th className="text-left p-2">Required</th>
                        <th className="text-left p-2">Member</th>
                        <th className="text-center p-2">Coverage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {requirementResults.map((r, idx) => (
                        <tr key={idx} className="hover:bg-app">
                          <td className="p-2 font-medium text-slate-700">{r.position}</td>
                          <td className="p-2 text-slate-500">{r.department}</td>
                          <td className="p-2 text-xs text-slate-500">{r.required.join(", ").slice(0, 60)}</td>
                          <td className="p-2 text-slate-600">{r.member}</td>
                          <td className="p-2 text-center">
                            {r.coverage === "yes" && <CheckCircle className="w-4 h-4 text-green-500 inline" />}
                            {r.coverage === "no" && <AlertTriangle className="w-4 h-4 text-amber-500 inline" />}
                            {r.coverage === "unfilled" && <Badge variant="outline" className="text-xs">Unfilled</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            ) : (
              <div className="text-center p-12 text-slate-400">Search for a skill or qualification to see position requirements and coverage</div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
